import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import {
  AmountWithAux,
  Button,
  DataTable,
  Group,
  Input,
  Modal,
  Paper,
  RowMenuItem,
  Select,
  Stack,
  Text,
  openConfirmDelete,
} from '@wapharma/design-system';
import { apiDelete, apiPatch, apiPost } from '../../api';
import { apiGetListSimple } from '../../apiList';
import { getDisplayError } from '../../apiErrors';
import { getToken } from '../../auth';
import { useToast } from '../../contexts/ToastContext';
import { useClientTablePagination } from '../../hooks/useClientTablePagination';
import { slugify } from '../../slugify';

type CampaignRow = {
  id: string;
  name: string;
  slug: string;
  startsAt: string | null;
  endsAt: string | null;
  discountPercent: unknown;
  _count?: { products: number };
};

type ProductOption = { id: string; name: string; slug: string };

export type CatalogCampaignsTabHandle = {
  reload: () => void;
};

export const CatalogCampaignsTab = forwardRef<CatalogCampaignsTabHandle>(function CatalogCampaignsTab(_props, ref) {
  const token = getToken();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [productOpts, setProductOpts] = useState<ProductOption[]>([]);
  const [campName, setCampName] = useState('');
  const [campDisc, setCampDisc] = useState('');
  const [campQuickCreateOpen, setCampQuickCreateOpen] = useState(false);
  const [linkCamp, setLinkCamp] = useState('');
  const [linkProd, setLinkProd] = useState(() => searchParams.get('campaignProduct') ?? '');
  const [filterCampName, setFilterCampName] = useState('');
  const [filterCampSlug, setFilterCampSlug] = useState('');
  const [campPending, setCampPending] = useState(false);
  const [linkPending, setLinkPending] = useState(false);
  const [editCamp, setEditCamp] = useState<CampaignRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editDisc, setEditDisc] = useState('');
  const [editStartsAt, setEditStartsAt] = useState('');
  const [editEndsAt, setEditEndsAt] = useState('');
  const [editPending, setEditPending] = useState(false);

  const loadCampaigns = useCallback(() => {
    if (!token) return;
    void apiGetListSimple<CampaignRow>('/catalog/admin/campaigns', token)
      .then(setCampaigns)
      .catch(() => setCampaigns([]));
  }, [token]);

  const loadProductOpts = useCallback(() => {
    if (!token) return;
    void apiGetListSimple<ProductOption>('/catalog/admin/products/options', token)
      .then(setProductOpts)
      .catch(() => setProductOpts([]));
  }, [token]);

  useEffect(() => {
    loadCampaigns();
    loadProductOpts();
  }, [loadCampaigns, loadProductOpts]);

  useImperativeHandle(ref, () => ({ reload: loadCampaigns }), [loadCampaigns]);

  useEffect(() => {
    const cp = searchParams.get('campaignProduct');
    if (cp) setLinkProd(cp);
  }, [searchParams]);

  const campaignContextProductName = useMemo(() => {
    if (!linkProd) return null;
    return productOpts.find((p) => p.id === linkProd)?.name ?? null;
  }, [linkProd, productOpts]);

  const filteredCampaigns = useMemo(() => {
    const n = filterCampName.trim().toLowerCase();
    const s = filterCampSlug.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (n && !c.name.toLowerCase().includes(n)) return false;
      if (s && !c.slug.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [campaigns, filterCampName, filterCampSlug]);

  const { pagination: campaignsPagination } = useClientTablePagination(25, [
    filterCampName,
    filterCampSlug,
    campaigns,
  ]);

  const closeCampQuickCreate = () => {
    setCampQuickCreateOpen(false);
    setCampName('');
    setCampDisc('');
  };

  const createCampaign = () => {
    if (!token || !campName.trim() || campPending) return;
    setCampPending(true);
    void apiPost(
      '/catalog/admin/campaigns',
      {
        name: campName.trim(),
        slug: slugify(campName.trim()) || 'campagne',
        discountPercent: campDisc.trim() ? Number(campDisc.replace(',', '.')) : undefined,
      },
      token,
    )
      .then(() => {
        closeCampQuickCreate();
        loadCampaigns();
        showToast('Campagne créée.', { tone: 'success' });
      })
      .catch((e) => showToast(getDisplayError(e), { tone: 'error' }))
      .finally(() => setCampPending(false));
  };

  const linkCampaignProduct = () => {
    if (!token || !linkCamp || !linkProd || linkPending) return;
    setLinkPending(true);
    void apiPost(`/catalog/admin/campaigns/${linkCamp}/products`, { productId: linkProd }, token)
      .then(() => {
        setLinkProd('');
        loadCampaigns();
        const next = new URLSearchParams(searchParams);
        next.delete('campaignProduct');
        setSearchParams(next, { replace: true });
        showToast('Produit lié à la campagne.', { tone: 'success' });
      })
      .catch((e) => showToast(getDisplayError(e), { tone: 'error' }))
      .finally(() => setLinkPending(false));
  };

  const openEdit = (c: CampaignRow) => {
    setEditCamp(c);
    setEditName(c.name);
    setEditDisc(c.discountPercent != null ? String(c.discountPercent) : '');
    setEditStartsAt(c.startsAt ? c.startsAt.slice(0, 16) : '');
    setEditEndsAt(c.endsAt ? c.endsAt.slice(0, 16) : '');
  };

  const saveEdit = () => {
    if (!token || !editCamp || editPending) return;
    setEditPending(true);
    void apiPatch(
      `/catalog/admin/campaigns/${editCamp.id}`,
      {
        name: editName.trim() || editCamp.name,
        startsAt: editStartsAt.trim() ? new Date(editStartsAt).toISOString() : null,
        endsAt: editEndsAt.trim() ? new Date(editEndsAt).toISOString() : null,
        discountPercent: editDisc.trim() ? Number(editDisc.replace(',', '.')) : null,
      },
      token,
    )
      .then(() => {
        setEditCamp(null);
        loadCampaigns();
        showToast('Campagne mise à jour.', { tone: 'success' });
      })
      .catch((e) => showToast(getDisplayError(e), { tone: 'error' }))
      .finally(() => setEditPending(false));
  };

  const deleteCampaign = (c: CampaignRow) => {
    if (!token) return;
    openConfirmDelete({
      title: 'Supprimer la campagne',
      message: `Supprimer « ${c.name} » ?`,
      onConfirm: () =>
        apiDelete(`/catalog/admin/campaigns/${c.id}`, token).then(() => {
          loadCampaigns();
          showToast('Campagne supprimée.', { tone: 'success' });
        }),
    });
  };

  return (
    <>
      <Stack gap="md">
        {linkProd ? (
          <Paper withBorder p="sm" radius="md" style={{ background: 'var(--wp-surface-muted)' }}>
            <Text size="sm">
              Produit ciblé : <strong>{campaignContextProductName ?? `${linkProd.slice(0, 8)}…`}</strong>
            </Text>
            <Text size="xs" c="dimmed" style={{ marginTop: 'var(--wp-space-6)' }}>
              Choisissez une campagne puis « Lier produit ». Retour au produit : vue Produits, puis la ligne du
              produit.
            </Text>
          </Paper>
        ) : null}
        <Text size="sm" c="dimmed">
          Lier un produit après création (réduction % facultative sur la campagne).
        </Text>
        <Group align="flex-end">
          <Select
            label="Campagne"
            options={[{ value: '', label: '—' }, ...campaigns.map((c) => ({ value: c.id, label: c.name }))]}
            value={linkCamp}
            onChange={(e) => setLinkCamp(e.currentTarget.value)}
          />
          <Select
            label="Produit"
            options={[{ value: '', label: '—' }, ...productOpts.map((p) => ({ value: p.id, label: p.name }))]}
            value={linkProd}
            onChange={(e) => setLinkProd(e.currentTarget.value)}
          />
          <Button type="button" onClick={linkCampaignProduct} disabled={!linkCamp || !linkProd || linkPending}>
            {linkPending ? 'Liaison…' : 'Lier produit'}
          </Button>
        </Group>
        <Group justify="flex-end">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            disabled={!token}
            onClick={() => {
              setCampName('');
              setCampDisc('');
              setCampQuickCreateOpen(true);
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconPlus size={14} stroke={1.5} aria-hidden />
              Création rapide
            </span>
          </Button>
        </Group>
        <DataTable<CampaignRow>
          variant="list"
          pagination={campaignsPagination}
          pinStartColumnKey="name"
          columns={[
            {
              key: 'name',
              label: 'Nom',
              headerFilterKind: 'search',
              headerFilterActive: Boolean(filterCampName.trim()),
              onHeaderFilterReset: () => setFilterCampName(''),
              headerFilterResetAriaLabel: 'Réinitialiser recherche nom',
              headerFilter: (
                <Input
                  placeholder="Rechercher…"
                  value={filterCampName}
                  onChange={(e) => setFilterCampName(e.target.value)}
                  aria-label="Rechercher par nom"
                />
              ),
            },
            {
              key: 'slug',
              label: 'Slug',
              headerFilterKind: 'search',
              headerFilterActive: Boolean(filterCampSlug.trim()),
              onHeaderFilterReset: () => setFilterCampSlug(''),
              headerFilterResetAriaLabel: 'Réinitialiser recherche slug',
              headerFilter: (
                <Input
                  placeholder="Rechercher…"
                  value={filterCampSlug}
                  onChange={(e) => setFilterCampSlug(e.target.value)}
                  aria-label="Rechercher par slug"
                />
              ),
            },
            {
              key: 'disc',
              label: 'Réduction',
              render: (r) => <AmountWithAux value={r.discountPercent} auxiliary="%" decimals={0} />,
            },
            {
              key: 'window',
              label: 'Validité',
              render: (r) => {
                const start = r.startsAt ? new Date(r.startsAt).toLocaleDateString('fr-FR') : '—';
                const end = r.endsAt ? new Date(r.endsAt).toLocaleDateString('fr-FR') : '—';
                return `${start} → ${end}`;
              },
            },
            {
              key: 'n',
              label: 'Produits',
              render: (r) => String(r._count?.products ?? 0),
            },
          ]}
          data={filteredCampaigns}
          rowKey={(r) => r.id}
          emptyMessage="Aucune campagne."
          rowActions={(r) => (
            <>
              <RowMenuItem onClick={() => openEdit(r)}>Modifier</RowMenuItem>
              <RowMenuItem danger onClick={() => deleteCampaign(r)}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <IconTrash size={14} stroke={1.8} aria-hidden />
                Supprimer
              </span>
            </RowMenuItem>
            </>
          )}
        />
      </Stack>

      <Modal opened={Boolean(editCamp)} onClose={() => setEditCamp(null)} title={`Modifier ${editCamp?.name ?? ''}`}>
        <Stack gap="sm">
          <Input label="Nom" value={editName} onChange={(e) => setEditName(e.target.value)} />
          <Input label="Réduction %" value={editDisc} onChange={(e) => setEditDisc(e.target.value)} />
          <Input label="Début" type="datetime-local" value={editStartsAt} onChange={(e) => setEditStartsAt(e.target.value)} />
          <Input label="Fin" type="datetime-local" value={editEndsAt} onChange={(e) => setEditEndsAt(e.target.value)} />
          <Group justify="flex-end" wrap="wrap" gap="sm">
            <Button type="button" variant="ghost" onClick={() => setEditCamp(null)} disabled={editPending}>
              Annuler
            </Button>
            <Button type="button" variant="primary" onClick={saveEdit} disabled={editPending}>
              {editPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={campQuickCreateOpen} onClose={closeCampQuickCreate} title="Nouvelle campagne">
        <Stack gap="sm">
          <Input label="Nom" aria-label="Nom campagne" value={campName} onChange={(e) => setCampName(e.target.value)} />
          <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--wp-font-mono)' }}>
            Slug : {slugify(campName.trim()) || '—'}
          </Text>
          <Input
            label="Réduction %"
            aria-label="Réduction pourcent"
            value={campDisc}
            onChange={(e) => setCampDisc(e.target.value)}
          />
          <Group justify="flex-end" wrap="wrap" gap="sm">
            <Button type="button" variant="ghost" onClick={closeCampQuickCreate} disabled={campPending}>
              Annuler
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={createCampaign}
              disabled={!token || !campName.trim() || campPending}
            >
              {campPending ? 'Création…' : 'Créer'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
});
